import { useEffect, useState } from "react"

import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { DateTime } from "luxon"
import axios from "axios"
import toastr from "toastr"

import { TimerExtensionPopup } from "pages/ticket-details/timer-extension-popup"

import { useLocalStorage } from "hooks/utils/use-local-storage"
import { useNavigateWithParams } from "hooks/navigation/use-navigate-with-params"

const Timer = () => {
  const [timeLeft, setTimeLeft] = useState<string>("00:00")
  const [showExtensionPopup, setShowExtensionPopup] = useState(false)
  const [hasDeclinedExtension, setHasDeclinedExtension] = useState(false)
  const [isTimeCalculated, setIsTimeCalculated] = useState(false)
  const navigateWithParams = useNavigateWithParams()
  const { uuid } = useParams()
  const { localStorageData, setLocalStorageData } = useLocalStorage()
  const { t } = useTranslation()

  const handleExtendTime = async () => {
    if (localStorageData?.uuid) {
      try {
        const response = await axios.patch(
          `/api/registration/order/${localStorageData.uuid}/extend-order/`
        )

        setLocalStorageData({
          ...localStorageData,
          timestamp: DateTime.fromISO(response.data.expiration).toMillis(),
        })
      } catch (error: any) {
        toastr.error(
          String(error.response?.data?.message || error),
          "Failed to extend order time"
        )
        console.error("Failed to extend order time:", error)
      }
    }
    setShowExtensionPopup(false)
  }

  const handleDeclineExtension = () => {
    setShowExtensionPopup(false)
    setHasDeclinedExtension(true)
  }

  useEffect(() => {
    if (!localStorageData?.timestamp) {
      navigateWithParams(`/${uuid}/select-tickets`)
      return
    }

    const calculateTime = () => {
      const expirationTime = DateTime.fromMillis(localStorageData.timestamp)
      const now = DateTime.now()
      const diff = expirationTime.diff(now, ["minutes", "seconds"])

      const minutes = Math.floor(diff.minutes)
      const seconds = Math.floor(diff.seconds)

      if (minutes <= 0 && seconds <= 1) {
        setLocalStorageData(null)
        navigateWithParams(`/${uuid}/select-tickets`)
        return
      }

      if (minutes < 3 && !showExtensionPopup && !hasDeclinedExtension) {
        setShowExtensionPopup(true)
      }

      setTimeLeft(
        `${minutes.toString().padStart(2, "0")}:${seconds
          .toString()
          .padStart(2, "0")}`
      )
      setIsTimeCalculated(true)
    }

    calculateTime()
    const interval = setInterval(calculateTime, 1000)

    return () => clearInterval(interval)
  }, [
    localStorageData,
    navigateWithParams,
    uuid,
    setLocalStorageData,
    showExtensionPopup,
    hasDeclinedExtension,
  ])

  if (!isTimeCalculated) {
    return null
  }

  return (
    <div className="sticky top-0 z-40 bg-theme_accent_light text-theme_text py-2">
      <div className="container mx-auto px-4 text-center">
        <span className="font-semibold">{t("general.time_remaining")}: </span>
        <span className="font-mono text-lg">{timeLeft}</span>
      </div>
      {showExtensionPopup && (
        <TimerExtensionPopup
          onExtend={handleExtendTime}
          onClose={handleDeclineExtension}
        />
      )}
    </div>
  )
}

export { Timer }
